/**
 * F16.B: portal logo with monogram fallback. Reads the resolved logo URL
 * from `api.tenantBranding.getLogoUrl`. When the row has no logoStorageId,
 * or the URL is still loading, falls back to a 2-letter monogram derived
 * from `displayName`.
 *
 * Themed colors arrive via F16.B+ CSS variables. Until that ships, the
 * monogram uses the branding's `primaryColor` directly via a CSS custom
 * property, which keeps inline-style use to the minimum allowed by the
 * "Tailwind for new components" memory rule.
 */

import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { testIds } from '../../lib/testIds';

export type TenantLogoSize = 'titleBar' | 'landing';

const SIZE_PX: Record<TenantLogoSize, number> = {
  titleBar: 24,
  landing: 96,
};

/**
 * Derive a 2-letter uppercase monogram. "Clearview Legal AI" → "CL",
 * "Morrison" → "MO". Single-word names fall back to the first two letters.
 */
export function monogramFor(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) {return '??';}
  const tokens = trimmed.split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length >= 2) {
    return (tokens[0]![0]! + tokens[1]![0]!).toUpperCase();
  }
  const word = tokens[0] ?? trimmed;
  return word.slice(0, 2).toUpperCase();
}

export function TenantLogo({ size }: { size: TenantLogoSize }) {
  const { branding, slug } = useTenantBranding();
  // `useQuery` returns undefined while loading, null on miss.
  const logoUrl = useQuery(api.tenantBranding.getLogoUrl, { clientSlug: slug });
  const pixels = SIZE_PX[size];

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${branding.displayName} logo`}
        width={pixels}
        height={pixels}
        data-testid={testIds.portal.logo}
        className="object-contain bg-transparent"
        style={{ width: pixels, height: pixels }}
      />
    );
  }

  const monogram = monogramFor(branding.displayName);
  const fontPx = size === 'titleBar' ? 12 : 40;
  return (
    <span
      data-testid={testIds.portal.monogram}
      className="inline-flex items-center justify-center font-bold text-white select-none"
      style={{
        width: pixels,
        height: pixels,
        fontSize: fontPx,
        backgroundColor: branding.primaryColor,
        borderRadius: size === 'titleBar' ? 2 : 4,
      }}
      aria-label={`${branding.displayName} (no logo)`}
    >
      {monogram}
    </span>
  );
}
